// Canonical units-of-measure — the SINGLE source of truth for unit display.
//
// Two distinct fields, two vocabularies:
//   - base `unit` (SkuUnit, lowercase enum: ea/box/bag/ft/can/tube) — how on-hand is
//     COUNTED. STORED lowercase; compared in worker logic (e.g. `unit === 'ft'`). It is
//     NEVER recased in the DB — only uppercased at render via `displayUnit`.
//   - `purchaseUom` / `consumptionUom` (free-text uppercase: EA/LB/LF/SF/BAG/BOX/PC/...)
//     — the commercial Buy/Sell units, normalized for display via `normalizeUom`.
//
// `displayUnit` and `normalizeUom` share the same canonicalization today (uppercase +
// retire the BX/BG abbreviations → BOX/BAG); they're kept as two names so the call
// sites read by intent and can diverge later without a sweep.
import type { SkuUnit } from './types.js';

/** Full canonical display vocabulary (uppercase). */
export const CANONICAL_UOMS = [
  'EA', 'FT', 'LF', 'LB', 'SF', 'BAG', 'BOX', 'PC', 'CAN', 'TUBE',
] as const;
export type CanonicalUom = (typeof CANONICAL_UOMS)[number];

/** The countable base units — the only values "Count in" (base `unit`) may take.
 *  Mirrors the `SkuUnit` enum (types.ts). on-hand can only be counted in these;
 *  LF/LB/SF/PC are commercial-only and are NOT countable. */
export const COUNTABLE_UNITS: readonly SkuUnit[] = ['ea', 'box', 'bag', 'ft', 'can', 'tube'];

// Core canonicalizer: uppercase + collapse the retired abbreviations.
function toCanonical(raw: string | null | undefined): string {
  if (raw == null) return '';
  const u = raw.trim().toUpperCase();
  if (u === 'BX') return 'BOX';
  if (u === 'BG') return 'BAG';
  return u;
}

/** Display a base `unit` (lowercase enum) as its canonical uppercase label
 *  (ea→EA, box→BOX, bag→BAG, ft→FT, can→CAN, tube→TUBE). Render-only — never
 *  mutate the stored value. */
export function displayUnit(unit: string | null | undefined): string {
  return toCanonical(unit);
}

/** Canonicalize a purchase/consumption UoM string for display/filtering
 *  (uppercase; BX→BOX, BG→BAG; already-canonical values unchanged). */
export function normalizeUom(raw: string | null | undefined): string {
  return toCanonical(raw);
}
